import { API_URL } from "env";
import { mixLogOut } from "../services/mixin";

/**
 * API Quản lý Facebook Ads (dữ liệu đồng bộ từ OAuth).
 *  - getFbAdAccounts: GET /api/v1/be/fb-ad-accounts     (search, is_active, per_page, page)
 *  - getFbCampaigns:  GET /api/v1/be/fb-campaigns       (ad_account_id, status, search, per_page, page, date_preset/date_from/date_to)
 *  - getFbAdSets:     GET /api/v1/be/fb-ad-sets         (campaign_id, ad_account_id, status, per_page, page) + summary_insights
 *  - getFbAds:        GET /api/v1/be/fb-ads             (ad_set_id, campaign_id, ad_account_id, status, per_page, page) + summary_insights
 */

export interface FbInsights {
    impressions: number;
    reach: number;
    clicks: number;
    spend: number;
    leads: number;
    revenue: number;
    roas: number;
    ctr: number;
    cpc: number;
    cpm: number;
    cvr: number;
    cpl: number;
}

export interface FbAdAccount {
    id: number;
    fb_account_id: string;
    account_id: string;
    name: string;
    account_status: number;
    currency: string;
    timezone_name: string;
    is_active: number;
    campaigns_count: number;
}

export interface FbCampaign {
    id: number;
    fb_campaign_id: string;
    ad_account_id: number;
    name: string;
    status: string;
    effective_status: string;
    objective: string | null;
    buying_type: string | null;
    bid_strategy: string | null;
    daily_budget: number | null;
    lifetime_budget: number | null;
    budget_remaining: number | null;
    start_time: string | null;
    stop_time: string | null;
    created_at: string;
    updated_at: string;
    ad_sets_count: number;
    ads_count: number;
    insights?: FbInsights | null;
}

export interface FbAdSet {
    id: number;
    fb_ad_set_id: string;
    campaign_id: number;
    ad_account_id: number;
    name: string;
    status: string;
    effective_status: string;
    optimization_goal: string | null;
    daily_budget: number | null;
    lifetime_budget: number | null;
    start_time: string | null;
    end_time: string | null;
    ads_count: number;
    insights?: FbInsights | null;
}

export interface FbAd {
    id: number;
    fb_ad_id: string;
    ad_set_id: number;
    campaign_id: number;
    ad_account_id: number;
    name: string;
    status: string;
    effective_status: string;
    creative_id: string | null;
    creative_name: string | null;
    created_at: string;
    updated_at: string;
    insights?: FbInsights | null;
}

const authHeaders = () => ({
    Accept: "application/json",
    Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});

const buildQuery = (params: Record<string, any>) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== "") q.append(k, String(v));
    });
    return q.toString();
};

async function fetchList<T>(
    path: string,
    params: Record<string, any>
): Promise<{ status: boolean; items: T[]; total: number; summary_insights?: FbInsights | null; msg?: string }> {
    try {
        const res = await fetch(`${API_URL}${path}?${buildQuery(params)}`, { headers: authHeaders() });
        if (!res.ok) {
            if (res.status === 401) { mixLogOut(); }
            throw new Error(`HTTP ${res.status}`);
        }
        const json = await res.json();
        const d = json.data || {};
        return {
            status: !!json.status,
            items: d.data || [],
            total: Number(d.total || 0),
            summary_insights: json.summary_insights || null,
            msg: json.message,
        };
    } catch (e: any) {
        return { status: false, items: [], total: 0, summary_insights: null, msg: e?.message };
    }
}

export const getFbAdAccounts = (params: {
    search?: string; is_active?: number; per_page?: number; page?: number;
} = {}) => fetchList<FbAdAccount>("/api/v1/be/fb-ad-accounts", params);

export const getFbCampaigns = (params: {
    ad_account_id?: number; status?: string; search?: string; per_page?: number; page?: number;
    date_preset?: string; date_from?: string; date_to?: string;
} = {}) => fetchList<FbCampaign>("/api/v1/be/fb-campaigns", params);

export const getFbAdSets = (params: {
    campaign_id?: number; ad_account_id?: number; status?: string; per_page?: number; page?: number;
    date_preset?: string; date_from?: string; date_to?: string;
} = {}) => fetchList<FbAdSet>("/api/v1/be/fb-ad-sets", params);

export const getFbAds = (params: {
    ad_set_id?: number; campaign_id?: number; ad_account_id?: number; status?: string; per_page?: number; page?: number;
    date_preset?: string; date_from?: string; date_to?: string;
} = {}) => fetchList<FbAd>("/api/v1/be/fb-ads", params);
